import { ExternalResourceCount } from '../models/external-result.count';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Record, RecordAvailability, RecordSuggestion } from 'src/app/models/record';
import { RecordRequest } from 'src/app/models/record-request';
import { Collection } from 'src/app/models/collection';
import { Checkout } from 'src/app/state/user/checkouts/models/checkout';
import { CheckoutHistory } from '../models/checkout-history';
import { RecordPreview } from 'src/app/models/record-preview';
import { AcceptHoldResponse } from 'src/app/state/user/holds/models/hold';
import { RecordResult } from 'src/app/models/record-result';
import { CheckoutDownload } from 'src/app/models/checkout-download';
import { Recommendation, RecommendationType } from '../models/recommendation';
import {
  AvailableFilter,
  ExternalLoginType,
  RemoteLoginResponse,
  SimpleLocation,
  User,
  UserBadge,
  UserFilter,
  UserGroup,
  UserGroupCollectionsResponse,
  UserSignUp,
  VerifyAccountStatus,
} from '../models/user';
import { Rating } from '../models/rating';
import { Comment } from '../models/comment';
import { Notification } from '../models/notification';
import { UserStatsBundle } from '../models/statistics';
import { Device, VISITOR_ID } from '../models/device';
import { FollowedList, OtkList } from '../models/user-item-list';
import { UserItem } from '../models/user-item';
import { Footer } from '../models/footer';
import { ImageResource } from '../models/image-resource';
import {
  ClientConfiguration,
  RegisterOption,
  RegisterParameter,
  RemoteLoginData,
} from '../models/client-configuration';
import { Filter } from '../models/filter';
import { Token } from '../models/token';
import { catchError, map, tap } from 'rxjs/operators';
import { PaginatedResult, Util } from '../util';
import { FromScreen } from '../models/from-screen';
import { LoggerService } from './logger.service';
import { PurchaseSuggestion } from '../models/purchase-suggestion';
import { CssVar } from '../models/css-var';
import { Tutor } from '../models/tutor';
import { VisualizationHistory, VisualizationHistoryItem, VisualizationType } from '../models/visualization-history';
import { Error } from '../models/error';
import { Redirection } from '../models/redirection';
import { UserResponse } from '../models/user-interests';
import { Challenge, CreateChallengeData } from '../models/challenge';
import { YoutubeResponse } from '../models/youtube-response';
import { FingerprintService } from './fingerprint.service';
import { FollowedAuthor } from '../models/followed-author';
import { Cep } from '../models/cep';
import { Invitation } from '../models/invitation/invitation';
import { InvitationsRequest } from '../models/invitation/invitations-request';
import { InvitationsResponse } from '../models/invitation/invitations-response';
import { RssItem } from '../models/rss';
import { HistoryItemType, HistoryUser } from '../models/history-user';
import { Cite } from '../models/cite';
import { PhysicalRecordResult } from '../models/physical-record';
import { OleConfiguration } from '../models/ole-configuration';
import { BookClubCarousel } from '../models/bookclubcarousel';
import { ResourceProgress } from '../models/resource-progress';
import { ResourceTimeReading } from '../models/resource-time-reading';
import { RecordExternal } from '../models/record-external';
import { ConsumptionTimeRequest } from '../components/user/user-history/models/consumption-time-request.model';

const HTTP_OPTIONS_URLENCODED = {
  headers: new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  API_URL: string;
  MAX_BOOK_CLUBS: number;

  constructor(
    private logger: LoggerService,
    private _http: HttpClient,
    private fingerprintService: FingerprintService,
  ) {
    const url = new URL(Util.getOrigin());
    this.API_URL = `${url.origin}/opac/api/v2`;
    this.MAX_BOOK_CLUBS = 15;

    this.logger.info('Client API', this.API_URL);
  }

  public getToken(clientId: string, clientSecret: string): Observable<Token> {
    const basicAuth: string = btoa(clientId + ':' + clientSecret);

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Basic ${basicAuth}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    return this._http.post<Token>(`${this.API_URL}/token`, 'grant_type=client_credentials', httpOptions);
  }

  public remoteLogin(queryParams: any): Observable<RemoteLoginResponse> {
    return this._http
      .post<User | Redirection>(`${this.API_URL}/login/remote`, {}, { params: queryParams, observe: 'response' })
      .pipe(
        map((response) => {
          const remoteResponse: RemoteLoginResponse = {};

          if ((response.body as Redirection).url) {
            // TODO: legacy...
            remoteResponse.redirectTo = (response.body as Redirection).url;
          } else {
            remoteResponse.user = response.body as User;
            remoteResponse.redirectTo = response.headers.get('ng-opac-redirect');
          }

          return remoteResponse;
        }),
        tap(async (response) => {
          this.logger.info('Logged user', response.user);
          localStorage.setItem(VISITOR_ID, await this.fingerprintService.getVisitorId());
        })
      );
  }

  public getRemoteLoginUrl(userId: string): Observable<RemoteLoginData> {
    return this._http.get<RemoteLoginData>(`${this.API_URL}/patrons/${userId}/remote-login-url`);
  }

  public getExternalLoginUrl(callback: string, type: ExternalLoginType): Observable<string> {
    const queryParams = {
      callback: callback,
      type: type,
      client: 'opac',
    };

    return this._http.get<string>(`${this.API_URL}/login/external`, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  public externalLogin(body: any, type: ExternalLoginType): Observable<User> {
    const httpOptions = {
      params: {
        type: type,
        client: 'opac',
      },
    };

    let url = `${this.API_URL}/login/external`;

    if (type === 'EZPROXY') {
      httpOptions['headers'] = new HttpHeaders({
        'x-username': body['x-username'],
      });

      const appId = body['app'];

      if (appId) {
        url += `?app=${appId}`;
      }
    }

    return this._http.post<User>(url, body, httpOptions).pipe(
      tap(async (user) => {
        this.logger.info('Logged user', user);
        localStorage.setItem(VISITOR_ID, await this.fingerprintService.getVisitorId());
      })
    );
  }

  public logout(): Observable<any> {
    return this._http.get(`${this.API_URL}/logout`);
  }

  public forgot(identifier: string): Observable<any> {
    return this._http.post(`${this.API_URL}/login/forgot`, `identifier=${identifier}`, HTTP_OPTIONS_URLENCODED);
  }

  public resetPassword(code: string, password: string): Observable<any> {
    return this._http.post<any>(
      `${this.API_URL}/login/reset-password`,
      `code=${code}&password=${password}`,
      HTTP_OPTIONS_URLENCODED
    );
  }

  public isChpwdValid(code: string): Observable<any> {
    return this._http.post<any>(`${this.API_URL}/login/check-passwordKey`, `code=${code}`, HTTP_OPTIONS_URLENCODED);
  }

  public validateCode(code: string): Observable<any> {
    return this._http.get<any>(`${this.API_URL}/code/validate`, { params: { code: code } });
  }

  public register(userParameters: RegisterParameter[], multipleId?: number, code?: string): Observable<UserSignUp> {
    const queryParams = {};

    if (multipleId) {
      queryParams['multipleId'] = multipleId;
    }

    if (code) {
      queryParams['code'] = code;
    }

    return this._http.post<UserSignUp>(`${this.API_URL}/register`, userParameters, { params: queryParams });
  }

  public confirmUser(key: string): Observable<void> {
    return this._http.post<void>(`${this.API_URL}/register/confirm`, null, { params: { key: key } });
  }

  public getCollections(locale: string): Observable<Collection[]> {
    return this._http
      .get<Collection[]>(`${this.API_URL}/records/collections`, {
        params: { showExperiences: 'true', locale: `${locale}` },
      })
      .pipe(
        map((c) => {
          const collections = c;

          collections.forEach((col) => {
            col.fromUserList = col.topten === 'genericByListSelection';
            col.records = col.records.map((record) => Util.prepareRecord(record));

            if (col.fromUserList) {
              col.userLists.forEach((l) => {
                l.covers = Util.moveSelectedCoverToTop(
                  l.items.map((i) => i.coverUrl),
                  l.selectedCoverIdx
                );
              });
            }
          });

          return collections;
        })
      );
  }

  public getAllFilters(): Observable<Filter[]> {
    return this._http.get<Filter[]>(`${this.API_URL}/records/filters`, {
      params: { showExperiences: 'true', enableBiblioFacet: 'true', limitFacetValues: 16, },
    });
  }

  public getFilter(filterName: string, params: { [key: string]: string | boolean | number }): Observable<Filter> {
    return this._http.get<Filter>(`${this.API_URL}/records/filters/${filterName}`, {
      params
    });
  }

  public recordHit(recordId: string, title: string, from: FromScreen) {
    return this._http
      .post<any>(`${this.API_URL}/records/${recordId}/hit`, `title=${title}&from=${from}`, HTTP_OPTIONS_URLENCODED)
      .subscribe(() => { });
  }

  public getRecord(recordId: string, includeBcResources = false, enableMetadata = false): Observable<Record> {
    let queryParams: any = {};

    if (includeBcResources) {
      queryParams = { includeBcResources };
    }

    if (enableMetadata) {
      queryParams = { enableMetadata };
    }

    return this._http
      .get<Record>(`${this.API_URL}/records/${recordId}`, { params: queryParams })
      .pipe(map((r) => Util.prepareRecord(r)));
  }

  public getRecords(
    request?: RecordRequest,
    availability = true,
    enableBiblioFacet?: boolean,
  ): Observable<RecordResult> {
    request.faceted = true;
    request.availability = availability;
    request.lists = true;
    request.showExperiences = true;
    request.enableBiblioFacet = enableBiblioFacet;

    return this._getRecords(request);
  }

  public getCitation(recordId: string): Observable<Cite[]> {
    return this._http.get<Cite[]>(`${this.API_URL}/records/${recordId}/cite`);
  }

  public getRssItems(recordId: string, limit: number, offset: number) {
    const queryParams: any = {
      limit: limit,
      offset: offset,
    };

    return this._http
      .get<PaginatedResult<RssItem>>(`${this.API_URL}/records/${recordId}/items`, { params: queryParams })
      .pipe(tap((result) => result.items.forEach((item) => Util.prepareRssItem(item))));
  }

  public getRssItem(recordId: string, rhId: string) {
    return this._http.get<RssItem>(`${this.API_URL}/records/${recordId}/item/${rhId}`);
  }

  public getPurchaseSuggestionsBySearch(request?: RecordRequest): Observable<RecordResult> {
    request.faceted = true;
    request.oplace = true;

    return this._getRecords(request);
  }

  public getLearningExperiencesAssociatedToRecordId(recordId: string, userId: string): Observable<Record[]> {
    const request: RecordRequest = {
      limit: 1000,
      offset: 0,
      allRecords: true,
      showExperiences: true,
      query: `learningExperienceAssociatedNinfs_ss:${recordId} AND learningExperienceAssociatedUserIds_ss:${userId}`,
    };

    return this._http.get<Record[]>(`${this.API_URL}/records`, { params: <any>request });
  }

  private _getRecords(request?: RecordRequest): Observable<RecordResult> {
    if (request.recordIds && typeof request.recordIds !== 'string') {
      request.recordIds = request.recordIds.join(',');
    }

    if (!request.recordIds) {
      delete request.recordIds;
    }

    request.limitFacetValues = 16;

    return this._http.get<RecordResult>(`${this.API_URL}/records`, { params: <any>request }).pipe(
      map((r) => {
        if (r.records) {
          r.records = r.records.map((_r) => Util.prepareRecord(_r));
        }

        return r;
      })
    );
  }

  public getRecordSuggestions(query: string, filter?: string): Observable<RecordSuggestion[]> {
    const queryParams: any = {
      query: query,
      showExperiences: true,
    };

    if (filter) {
      queryParams['filter'] = filter;
    }

    return this._http.get<RecordSuggestion[]>(`${this.API_URL}/records/suggest`, { params: queryParams });
  }

  public getRecordAvailability(
    recordId: string,
    userId?: string,
    issueDate?: string,
    rhId?: string
  ): Observable<RecordAvailability> {
    if (rhId) {
      return this.getRssItemAvailability(recordId, rhId, userId);
    }

    const queryParams = {};

    if (userId) {
      queryParams['patronId'] = userId;
    }

    if (issueDate) {
      queryParams['issueDate'] = issueDate;
    }

    return this._http.get<RecordAvailability>(`${this.API_URL}/records/${recordId}/availability`, {
      params: queryParams,
    });
  }

  private getRssItemAvailability(
    recordContainerId: string,
    rhId: string,
    userId?: string
  ): Observable<RecordAvailability> {
    const queryParams = {};
    if (userId) {
      queryParams['userId'] = userId;
    }

    return this._http.get<RecordAvailability>(
      `${this.API_URL}/records/${recordContainerId}/items/${rhId}/availability`,
      { params: queryParams }
    );
  }

  public getRutInfo(rut: string, multipleId: number): Observable<RegisterOption[]> {
    const queryParams = {
      rut: rut,
      multipleId: multipleId.toString(),
    };
    return this._http.get<RegisterOption[]>(`${this.API_URL}/login/get-info-rut`, { params: queryParams });
  }

  public getUserCheckoutHistory(userId: string, offset?: number): Observable<CheckoutHistory[]> {
    const queryParams: any = {
      limit: 10,
    };

    if (offset) {
      queryParams.offset = offset;
    }

    return this._http.get<CheckoutHistory[]>(`${this.API_URL}/patrons/${userId}/checkouts/history`, {
      params: queryParams,
    });
  }

  public getVisualizationHistory(userId: string, offset?: number): Observable<VisualizationHistory> {
    const queryParams: any = {
      limit: 9,
    };

    if (offset) {
      queryParams.offset = offset;
    }

    return this._http.get<VisualizationHistory>(`${this.API_URL}/patrons/${userId}/visualizations/history`, {
      params: queryParams,
    });
  }

  public removeVisualizationFromHistory(
    userId: string,
    visualizationId: number,
    type: VisualizationType
  ): Observable<VisualizationHistoryItem> {
    const queryParams = {
      type: type,
    };

    return this._http.put<VisualizationHistoryItem>(
      `${this.API_URL}/patrons/${userId}/visualizations/${visualizationId}/hide`,
      {},
      { params: queryParams }
    );
  }

  public get24SymbolsLoginUrl(userId: string) {
    return this._http.get<{ url: string }>(`${this.API_URL}/patrons/${userId}/24symbols/login-url`);
  }

  public getRecordPreview(recordId: string, issueDate?: string, rhId?: string): Observable<RecordPreview> {
    let queryParams = {};

    if (issueDate) {
      queryParams = { issueDate: issueDate };
    }

    if (rhId) {
      queryParams = { rhId: rhId };
    }

    return this._http.get<RecordPreview>(`${this.API_URL}/records/${recordId}/preview`, { params: queryParams });
  }

  public getRecordInfo(recordId: string, format: string): Observable<ImageResource[]> {
    return this._http.get<ImageResource[]>(`${this.API_URL}/records/${recordId}/info`, { params: { format: format } });
  }

  public getPhysicalRecordInfo(recordId: string, format: string): Observable<PhysicalRecordResult> {
    return this._http.get<PhysicalRecordResult>(`${this.API_URL}/records/${recordId}/info`, {
      params: { format: format },
    });
  }

  public getRestrictedImage(recordId: string, path: string): Observable<Blob> {
    const ext = path.split('.').pop();

    const httpOptions = {
      headers: new HttpHeaders({
        Accept: `image/${ext}`,
      }),
      params: {
        path: path,
      },
    };

    return this._http.get<Blob>(`${this.API_URL}/records/${recordId}/image`, {
      ...httpOptions,
      responseType: 'blob' as 'json',
    });
  }

  public galleryHit(recordId: string, from: FromScreen) {
    return this._http
      .post<any>(`${this.API_URL}/records/${recordId}/image/view`, `from=${from}`, HTTP_OPTIONS_URLENCODED)
      .subscribe(() => { });
  }

  public remoteView(recordId: string, from: FromScreen) {
    return this._http
      .post<any>(`${this.API_URL}/records/${recordId}/remote/view`, `from=${from}`, HTTP_OPTIONS_URLENCODED)
      .subscribe(() => { });
  }

  public removeCheckoutFromHistory(userId: string, checkoutId: number): Observable<Checkout> {
    return this._http.put<Checkout>(`${this.API_URL}/patrons/${userId}/checkouts/${checkoutId}/hide`, {});
  }

  public downloadResource(
    checkoutId: string,
    userId: string,
    format: string,
    token: string,
    recordId?: string,
    learningExperienceId?: string,
    captchaToken?: string,
  ): Observable<CheckoutDownload> {
    const queryParams = {
      patronId: userId,
      format: format,
      token: token,
      redirect: 'no',
      extractUrl: 'yes',
      recordId: recordId,
      captchaResponse: captchaToken,
    };

    if (learningExperienceId) {
      queryParams['learningExperienceId'] = learningExperienceId;
    }

    return this._http.get<CheckoutDownload>(`${this.API_URL}/checkouts/${checkoutId}/download`, {
      params: queryParams,
    });
  }

  public downloadAttachedResource(
    recordId: string,
    from: FromScreen,
    userId?: string
  ): Observable<HttpEvent<HttpResponse<Blob>>> {
    let body = `fromScreen=${from}`;

    if (userId) {
      body += `&patronId=${userId}`;
    }

    return this._http.post<HttpResponse<Blob>>(`${this.API_URL}/records/${recordId}/download`, body, {
      ...HTTP_OPTIONS_URLENCODED,
      responseType: 'blob' as 'json',
      reportProgress: true,
      observe: 'events',
    });
  }

  public getUserRecommendations(userId: string, recordId: string): Observable<Recommendation[]> {
    const queryParams = { recordId };
    return this._http.get<Recommendation[]>(`${this.API_URL}/patrons/${userId}/recommendations`, {
      params: queryParams,
    });
  }

  public getRecordRelatedRecommendations(recordId: string, type: RecommendationType): Observable<Recommendation[]> {
    const queryParams: any = {
      type: type.toString(),
      showExperiences: true,
    };

    return this._http.get<Recommendation[]>(`${this.API_URL}/records/${recordId}/recommendations`, {
      params: queryParams,
    });
  }

  public acceptHoldByMail(uuid: string, signature: string): Observable<AcceptHoldResponse> {
    const queryParams = {
      uuid: uuid,
      signature: signature,
    };

    return this._http.post<AcceptHoldResponse>(`${this.API_URL}/holds/accept`, {}, { params: queryParams });
  }

  public updateUser(user: User): Observable<User> {
    return this._http.post<User>(`${this.API_URL}/patrons/${user.id}/config`, user);
  }

  public getUserPicture(userId: string): Observable<UserBadge> {
    return this._http.get<UserBadge>(`${this.API_URL}/picture`, { params: { userId: userId } });
  }

  public updateUserProfilePicture(userId: string, picture: File): Observable<User> {
    const formData = new FormData();
    formData.append('picture', picture);

    return this._http.post<User>(`${this.API_URL}/patrons/${userId}/picture`, formData);
  }

  public deleteUserProfilePicture(userId: string): Observable<User> {
    return this._http.delete<User>(`${this.API_URL}/patrons/${userId}/picture`);
  }

  public getRecordRating(recordId: string, isbn?: string, userId?: string): Observable<Rating> {
    const queryParams = {};

    if (isbn) {
      queryParams['isbn'] = isbn;
    }

    if (userId) {
      queryParams['userId'] = userId;
    }

    return this._http.get<Rating>(`${this.API_URL}/records/${recordId}/ratings`, { params: queryParams }).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          return of(Util.getEmptyRating());
        }

        throw err;
      })
    );
  }

  public getRecordRatings(recordId: string[], isbn?: string[], userId?: string): Observable<Rating[]> {
    const queryParams = {};

    if (recordId) {
      queryParams['recordIds'] = recordId.join(',');
    }

    if (isbn) {
      queryParams['isbns'] = isbn.join(',');
    }

    if (userId) {
      queryParams['userId'] = userId;
    }

    return this._http
      .get<Rating[]>(`${this.API_URL}/ratings`, { params: queryParams })
      .pipe(map((result) => result || []));
  }

  public rateRecord(recordId: string, userId: string, rating: number, isbn?: string): Observable<Rating> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
      params: isbn ? { isbn: isbn } : {},
    };

    return this._http.post<Rating>(
      `${this.API_URL}/records/${recordId}/ratings`,
      `userId=${userId}&rate=${rating}`,
      httpOptions
    );
  }

  public getRecordComments(recordId: string): Observable<Comment[]> {
    return this._http.get<Comment[]>(`${this.API_URL}/records/${recordId}/comments`);
  }

  public logNewUnhandledError(error: Error): Observable<void> {
    return this._http.post<void>(`${this.API_URL}/errors`, error);
  }

  public commentResource(recordId: string, userId: string, comment: string, parentId?: string): Observable<Comment> {
    comment = encodeURIComponent(comment);
    let body = `userId=${userId}&comment=${comment}`;

    if (parentId) {
      body += `&parentId=${parentId}`;
    }

    return this._http.post<Comment>(`${this.API_URL}/records/${recordId}/comments`, body, HTTP_OPTIONS_URLENCODED);
  }

  public deleteComment(commentId: string): Observable<Comment> {
    return this._http.delete<Comment>(`${this.API_URL}/records/comments/${commentId}`);
  }

  public reactToComment(recordId: string, commentId: string, userId: string, like: boolean): Observable<Comment> {
    return this._http.post<Comment>(
      `${this.API_URL}/records/${recordId}/comments/${commentId}/vote`,
      `userId=${userId}&like=${like}`,
      HTTP_OPTIONS_URLENCODED
    );
  }

  public getUserUnreadNotifications(userId: string): Observable<Notification[]> {
    return this._http.get<Notification[]>(`${this.API_URL}/patrons/${userId}/notifications/unread`);
  }

  public getUserNotifications(userId: string): Observable<Notification[]> {
    return this._http.get<Notification[]>(`${this.API_URL}/patrons/${userId}/notifications`);
  }

  public markNotificationAsRead(userId: string, notificationId: string): Observable<boolean> {
    return this._http.post<boolean>(`${this.API_URL}/patrons/${userId}/notifications/${notificationId}/read`, {});
  }

  public deleteNotification(userId: string, notificationId: string): Observable<boolean> {
    return this._http.delete<boolean>(`${this.API_URL}/patrons/${userId}/notifications/${notificationId}`);
  }

  public markAllNotificationsAsRead(userId: string): Observable<boolean> {
    return this._http.post<boolean>(`${this.API_URL}/patrons/${userId}/notifications/read`, []);
  }

  public deleteAllNotification(userId: string, listNotificationsIds: string[]): Observable<boolean> {
    return this._http.put<boolean>(`${this.API_URL}/patrons/${userId}/notifications`, listNotificationsIds);
  }

  public getUserStatsBundle(userId: string, resourceId?: string): Observable<UserStatsBundle> {
    let queryParams = {};

    if (resourceId) {
      queryParams = { resourceId: resourceId };
    }

    return this._http.get<UserStatsBundle>(`${this.API_URL}/patrons/${userId}/statsBundle`, { params: queryParams });
  }

  public getUserActiveDevices(userId: string): Observable<Device[]> {
    return this._http.get<Device[]>(`${this.API_URL}/patrons/${userId}/devices`);
  }

  public deactivateDevice(device: Device): Observable<Device> {
    const { type, ..._device } = device;

    return this._http.put<Device>(`${this.API_URL}/patrons/${device.userId}/devices/_dummy`, _device);
  }

  public getUserLists(userId: string): Observable<OtkList[]> {
    return this._http
      .get<OtkList[]>(`${this.API_URL}/patrons/${userId}/lists`, { params: { showExperiences: 'true' } })
      .pipe(map((sortedLists) => sortedLists.sort((a, b) => b.creationDate.valueOf() - a.creationDate.valueOf())));
  }

  public getPublicList(listId: number): Observable<OtkList> {
    return this._http.get<OtkList>(`${this.API_URL}/lists/${listId}`);
  }

  public getPublicLists(
    listsIds: number[],
    limit = 12,
    offset = 0
  ): Observable<PaginatedResult<OtkList>> {
    const params = {
      limit: limit.toString(),
      offset: offset.toString(),
    };
    return this._http.post<PaginatedResult<OtkList>>(`${this.API_URL}/lists`, listsIds, { params: params }).pipe(
      map((paginatedResult) => {
        paginatedResult.items.forEach(
          (l) =>
          (l.covers = Util.moveSelectedCoverToTop(
            l.items.map((i) => i.coverUrl),
            l.selectedCoverIdx
          ))
        );
        return paginatedResult;
      })
    );
  }

  public addToList(userId: string, listId: number, recordId: string): Observable<UserItem> {
    return this._http.post<UserItem>(
      `${this.API_URL}/patrons/${userId}/lists/${listId}`,
      `resourceId=${recordId}`,
      HTTP_OPTIONS_URLENCODED
    );
  }

  public removeFromList(userId: string, listId: number, itemId: number): Observable<UserItem> {
    return this._http.delete<UserItem>(`${this.API_URL}/patrons/${userId}/lists/${listId}/${itemId}`);
  }

  public deleteList(userId: string, listId: number): Observable<OtkList> {
    return this._http.delete<OtkList>(`${this.API_URL}/patrons/${userId}/lists/${listId}`);
  }

  public emptyList(userId: string, listId: number): Observable<OtkList> {
    return this._http.delete<OtkList>(`${this.API_URL}/patrons/${userId}/lists/${listId}/items`);
  }

  public getFollowedLists(userId: string, limit = 10, offset = 0) {
    return this._http.get<PaginatedResult<FollowedList>>(
      `${this.API_URL}/patrons/${userId}/lists/followed?limit=${limit}&offset=${offset}`
    );
  }

  public followList(userId: string, listId: number) {
    const body = `listId=${listId}`;
    return this._http.post(`${this.API_URL}/patrons/${userId}/lists/follow`, body, HTTP_OPTIONS_URLENCODED);
  }

  public unfollowList(userId: string, listId: number) {
    const body = `listId=${listId}`;
    return this._http.post(`${this.API_URL}/patrons/${userId}/lists/unfollow`, body, HTTP_OPTIONS_URLENCODED);
  }

  public getFollowedAuthorsForUser(userId: string) {
    return this._http
      .get<FollowedAuthor[]>(`${this.API_URL}/patrons/${userId}/authors/followed`)
      .pipe(map((followedAuthors) => followedAuthors));
  }

  public followAuthor(userId: string, author: string) {
    const body = `author=${author}`;
    return this._http.post<FollowedAuthor>(
      `${this.API_URL}/patrons/${userId}/authors/follow`,
      body,
      HTTP_OPTIONS_URLENCODED
    );
  }

  public unfollowAuthor(userId: string, author: string) {
    const body = `author=${author}`;
    return this._http.post(`${this.API_URL}/patrons/${userId}/authors/unfollow`, body, HTTP_OPTIONS_URLENCODED);
  }

  public unfollowAllAuthors(userId: string) {
    return this._http.post(`${this.API_URL}/patrons/${userId}/authors/unfollow-all`, {});
  }

  public getFooter(locale = 'es'): Observable<Footer[]> {
    return this._http.get<Footer[]>(`${this.API_URL}/footer`, {
      params: {
        locale
      }
    });
  }

  public getClientConfiguration(locale: string, patronId?: string): Observable<ClientConfiguration> {
    return this._http.get<ClientConfiguration>(
      `${this.API_URL}/configuration`,
      this.buildOptionsToGetClientConfiguration(locale, patronId)
    );
  }

  private buildOptionsToGetClientConfiguration(locale: string, patronId?: string) {
    const visitorId = localStorage.getItem(VISITOR_ID);
    const headers = visitorId == null ? null : new HttpHeaders({ [VISITOR_ID]: visitorId });

    let params = new HttpParams().set('locale', locale);
    if (patronId) {
      params = params.append('patronId', patronId);
    }

    return {
      params: params,
      headers: headers,
    };
  }

  public getClientStyles(theme: string): Observable<Map<string, CssVar[]>> {
    return this._http
      .get<string>(`${this.API_URL}/configuration/css?theme=${theme}`, { responseType: 'text' as 'json' })
      .pipe(
        map((css: string) => {
          const parsed = css
            .replace(new RegExp(/:root {|}/, 'g'), '')
            .split(';')
            .map((r) => r.split(':'))
            .filter((v) => v.length > 1)
            .map((v) => {
              if (v.length === 3) {
                v[1] = `${v[1]}:${v[2]}`;
                delete v[2];
              }

              return v;
            })
            .map((v) => {
              const value = v[1].trim();

              return {
                key: v[0].trim(),
                value: value,
                original: value,
                color: Util.getColorType(value),
              };
            });

          return Util.groupBy(parsed, 'key');
        })
      );
  }

  public getUserPurchaseSuggestions(userId: string): Observable<PurchaseSuggestion[]> {
    return this._http.get<PurchaseSuggestion[]>(`${this.API_URL}/patrons/${userId}/suggestions`).pipe(
      tap((pss) =>
        pss.forEach((ps) => {
          const icon =
            ps.resourceTypes && ps.resourceTypes.length > 0
              ? Util.resolveIcon(ps.resourceTypes[0])
              : Util.resolveIcon('');
          ps.icon = icon;
        })
      )
    );
  }

  public createUserPurchaseSuggestions(suggestion: PurchaseSuggestion): Observable<PurchaseSuggestion> {
    return this._http.post<PurchaseSuggestion>(`${this.API_URL}/patrons/${suggestion.userId}/suggestions`, suggestion);
  }

  public cancelUserPurchaseSuggestions(suggestion: PurchaseSuggestion): Observable<PurchaseSuggestion> {
    return this._http.delete<PurchaseSuggestion>(
      `${this.API_URL}/patrons/${suggestion.userId}/suggestions/${suggestion.id}`
    );
  }

  public getPurchaseSuggestions(recordId: string[]): Observable<Map<string, number>> {
    return this._http.get<Map<string, number>>(`${this.API_URL}/suggestions`, {
      params: { recordId: recordId.join(',') },
    });
  }

  public checkIfExistsInLibrary(isbn: string[]): Observable<Map<string, boolean>> {
    return this._http.get<Map<string, boolean>>(`${this.API_URL}/suggestions/check`, {
      params: { isbn: isbn.join(',') },
    });
  }

  public getTutorsEmails(userId: string): Observable<Tutor[]> {
    return this._http.get<Tutor[]>(`${this.API_URL}/patrons/${userId}/tutors`);
  }

  public setTutors(userId: string, tutors: Tutor[]): Observable<Tutor[]> {
    return this._http.post<Tutor[]>(`${this.API_URL}/patrons/${userId}/tutors/set`, tutors);
  }

  public getInvitations(): Observable<Invitation[]> {
    return this._http.get<Invitation[]>(`${this.API_URL}/invitations`);
  }

  public createInvitations(invitationsRequest: InvitationsRequest): Observable<InvitationsResponse> {
    return this._http.post<InvitationsResponse>(`${this.API_URL}/invitations`, invitationsRequest);
  }

  public forwardInvitation(invitationId: number): Observable<Invitation> {
    return this._http.get<Invitation>(`${this.API_URL}/invitations/forward/${invitationId}`);
  }

  /**
   * Get a one use link to open bookclub resource (learning experience or specific section)
   * @param params an object with the query params to send. <p>Params may contains:
   *  <ul>
   *  <li>patronId: userId</li>
   *  <li>clubId: bookclub identifier (if you want to open a learning experience)</li>
   *  <li>section: specific section in which the bookclub will be opened (if you want to open an specific section)</li>
   *  </ul>
   * @example
   *  const params = {patronId: '10000', clubId: '8d059a98-43d0-11ed-b878-0242ac120002'}
   *  this.apiService.getLinktoBookClub(params);
   */
  public getLinkToBookclub(params: { patronId?: string; clubId?: string; section?: string, xUsername?: string }): Observable<string> {
    const queryParams = {};

    Object.entries(params).filter(([key, value]) => {
      return key !== 'xUsername' && value != null
    }).forEach(([key, value]) => {
      queryParams[key] = value;
    });

    const httpParams = {
      responseType: 'text' as 'json',
      params: queryParams,
    }

    if (params.xUsername) {
      httpParams['headers'] = {
        'x-username': params.xUsername ?? '',
      }
    }

    return this._http.get<string>(`${this.API_URL}/login/bookclub`, httpParams);
  }

  public validateLocation(location: SimpleLocation): Observable<boolean> {
    const queryParams = {
      lat: location.lat.toString(),
      lng: location.lng.toString(),
    };

    return this._http.get<boolean>(`${this.API_URL}/location/validate`, { params: queryParams });
  }

  public getBookClubs(userId: string, maxBookClub?: number, onlyModerates?: boolean): Observable<BookClubCarousel[]> {
    const queryParams = {};
    if (userId && maxBookClub !== undefined) {
      queryParams['maxBookClubsPerCarousel'] = maxBookClub;
    } else if (userId) {
      queryParams['maxBookClubsPerCarousel'] = this.MAX_BOOK_CLUBS;
    }
    if (onlyModerates !== undefined) {
      queryParams['onlyModerates'] = onlyModerates;
    }
    return this._http.get<BookClubCarousel[]>(`${this.API_URL}/patrons/${userId}/experiences`, { params: queryParams });
  }

  public getOleConfiguration(userId: string): Observable<OleConfiguration> {
    const queryParams = {};

    if (userId) {
      queryParams['patronId'] = userId;
    }

    return this._http.get<OleConfiguration>(`${this.API_URL}/ole-configuration`, { params: queryParams });
  }

  public getUserInterests(userId: string): Observable<UserResponse[]> {
    return this._http.get<UserResponse[]>(`${this.API_URL}/patrons/${userId}/responses`);
  }

  public setUserInterests(userId: string, responses: UserResponse[]): Observable<string> {
    return this._http.post<string>(`${this.API_URL}/patrons/${userId}/responses/policy`, responses);
  }

  public setDontShowInterests(userId: string): Observable<void> {
    return this._http.post<void>(`${this.API_URL}/patrons/${userId}/responses/skip`, {});
  }

  public verifyCaptchaResponse(captchaResponse: string): Observable<any> {
    return this._http.get<any>(`${this.API_URL}/captcha/verify`, { params: { captchaResponse: captchaResponse } });
  }

  public getPublicActiveChallenges(): Observable<Challenge[]> {
    return this._http.get<Challenge[]>(`${this.API_URL}/challenges/active`);
  }

  public getUserChallenges(userId: string): Observable<Challenge[]> {
    return this._http.get<Challenge[]>(`${this.API_URL}/patrons/${userId}/challenges`);
  }

  public createUserChallenge(userId: string, challenge: CreateChallengeData): Observable<Challenge> {
    return this._http.post<Challenge>(`${this.API_URL}/patrons/${userId}/challenges`, challenge);
  }

  public deleteUserChallenge(userId: string, challenge: Challenge): Observable<Challenge> {
    return this._http.delete<Challenge>(`${this.API_URL}/patrons/${userId}/challenges/${challenge.id}`);
  }

  public getExternalResourceCount(query: string): Observable<ExternalResourceCount> {
    const queryParams = {};

    queryParams['query'] = query;

    return this._http.get<ExternalResourceCount>(`${this.API_URL}/externals/count`, { params: queryParams });
  }

  public getYoutubeResources(query: string, pageToken?: string): Observable<YoutubeResponse> {
    const queryParams = {};

    queryParams['query'] = query;

    if (pageToken) {
      queryParams['pageToken'] = pageToken;
    }

    return this._http.get<YoutubeResponse>(`${this.API_URL}/externals/youtube`, { params: queryParams });
  }

  public verifyAccount(token: string): Observable<VerifyAccountStatus> {
    return this._http.get<VerifyAccountStatus>(`${this.API_URL}/verify-account/${token}`);
  }

  public createGuestAccount(token: string): Observable<User> {
    return this._http.get<User>(`${this.API_URL}/invitations/create-guest-account/${token}`);
  }

  public async getCep(url: string, cep: string): Promise<Cep> {
    return await this._http.get<Cep>(`${url}/${cep}`).toPromise();
  }

  public redirectToCastLogin(): Observable<any> {
    const queryParams = {};
    queryParams['service'] = 'https://bibliotecapais.ceibal.edu.uy/opac/authCAS?app=opac';

    return this._http.get<any>(`https://ingreso.ceibal.edu.uy/login?`, {
      params: queryParams,
      withCredentials: true,
      observe: 'response',
      responseType: 'text' as 'json',
    });
  }

  public getUserHistory(page?: number, size?: number): Observable<HistoryUser> {
    const queryParams: any = {
      page: 0,
      size: 10,
    };
    if (page) {
      queryParams.page = page;
    }
    if (size) {
      queryParams.size = size;
    }
    return this._http.get<HistoryUser>(`${this.API_URL}/user-history`, { params: queryParams });
  }

  public getHistoryResourcesProgress(page?: number, size?: number): Observable<ResourceProgress[]> {
    const queryParams: any = {
      page: 0,
      size: 10,
    };
    if (page) {
      queryParams.page = page;
    }
    if (size) {
      queryParams.size = size;
    }
    return this._http.get<ResourceProgress[]>(`${this.API_URL}/user-history/progress`, { params: queryParams });
  }

  public hideHistoryItem(recordId: string, type: HistoryItemType): Observable<void> {
    const queryParams: any = {
      recordId: recordId,
      type: type,
    };
    return this._http.put<void>(`${this.API_URL}/user-history/hide-item`, null, { params: queryParams });
  }

  public hideAllHistoryItems(): Observable<void> {
    return this._http.put<void>(`${this.API_URL}/user-history/hide-all`, null);
  }

  public getGroups(userId: string): Observable<UserGroupCollectionsResponse> {
    return this._http.get<UserGroupCollectionsResponse>(`${this.API_URL}/group/${userId}/groups`);
  }

  public getUserGroupById(groupId: string): Observable<UserGroup> {
    return this._http.get<UserGroup>(`${this.API_URL}/group/${groupId}`);
  }

  public deleteGroup(id: string): Observable<boolean> {
    return this._http.delete<boolean>(`${this.API_URL}/group/${id}`);
  }

  public createGroup(userGroup: UserGroup): Observable<HttpResponse<{ users_not_found: string[], managers_not_found: string[] }>> {
    return this._http.post<{ users_not_found: string[], managers_not_found: string[] }>(`${this.API_URL}/group`, userGroup, { observe: 'response' });
  }

  public updateGroupEmail(userGroup: UserGroup): Observable<{ users_not_found: string[], managers_not_found: string[] }> {
    return this._http.put<{ users_not_found: string[], managers_not_found: string[] }>(`${this.API_URL}/group`, userGroup);
  }

  public sendInactiveUsers(groupId: string, users: string[], managers: string[], userId: string): Observable<void> {
    return this._http.post<void>(`${this.API_URL}/group/inactive-users`, { groupId, usernames: users, managerNames: managers, addBy: userId });
  }

  public groupsFilters(): Observable<AvailableFilter[]> {
    return this._http.get<AvailableFilter[]>(`${this.API_URL}/group/filters`);
  }

  public getUsersByFilters(filter: UserFilter[]): Observable<string[]> {
    return this._http.post<string[]>(`${this.API_URL}/group/usersByFilters`, filter);
  }

  public getPatronCheckoutsProgress(userId: string): Observable<ResourceProgress[]> {
    return this._http.get<ResourceProgress[]>(`${this.API_URL}/patrons/${userId}/checkouts/progress`);
  }

  public getConsumptionTime(
    userId: string,
    consumptionTimeRequest: ConsumptionTimeRequest[],
    startDate: Date
  ): Observable<ResourceTimeReading[]> {
    const queryParams: any = {
      start: startDate,
    };

    return this._http.post<ResourceTimeReading[]>(
      `${this.API_URL}/patrons/${userId}/new-consumption-time`,
      consumptionTimeRequest,
      { params: queryParams }
    );
  }

  public getExternalLink(recordId: string): Observable<RecordExternal> {
    return this._http.get<RecordExternal>(`${this.API_URL}/records/${recordId}/external`);
  }

  public downloadCertificate(path: string): Observable<string> {
    const queryParams = {
      path: path,
    };

    return this._http.get<string>(`${this.API_URL}/records/certificate-signed-url`, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  public saveUserRecommendationDialogLastAction(userId: string, action: 'accept' | 'reject'): Observable<void> {
    return this._http.post<void>(
      `${this.API_URL}/patrons/${userId}/recommendation-dialog/last-action?action=${action}`,
      {}
    );
  }
}
